import { computed, ref } from "vue";
import { defineStore } from "pinia";
import TrustSiteApi from "@dashboard/ajax/TrustSiteApi";
import {
  BANNER_NOTIFICATION_EMERGENCY,
  BANNER_NOTIFICATION_MAINTENANCE,
  type Banner,
} from "@entity/Banner";
import { createRequestGate } from "@dashboard/utils/requestGate";

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }

  return String(err);
}

function extractBannerGroups(payload: unknown): Banner[][] {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.filter(Array.isArray) as Banner[][];
}

/**
 * isLoading/error cover banner requests only. useDashboardSession exposes status
 * for the complete report-and-banners loading cycle.
 */
export const useBannerStore = defineStore("banner", () => {
  const bannerGroups = ref<Banner[][]>([]);
  const isVisible = ref(true);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const bannerRequests = createRequestGate();

  // Vue 2 stored the raw statusBanner payload on this.banners (Banner[][]).
  // CoreServicesRc then did this.banners[0].filter(...) inside its emergency /
  // maintenance getters. Vue 3 templates should use these projections: banners
  // is already the first group (Banner[]), and emergencyReport / maintenanceReport
  // are the filtered lists. Do not write banners[0].filter — [0] would be one
  // banner. The raw nested payload stays on bannerGroups. banners is also empty
  // when isVisible is false; Vue 2 had no such switch.
  const banners = computed(() => (isVisible.value ? bannerGroups.value[0] ?? [] : []));
  const hasBanners = computed(() => banners.value.length > 0);

  const emergencyReport = computed(() =>
    banners.value.filter((banner) => banner.notificationType === BANNER_NOTIFICATION_EMERGENCY));

  const maintenanceReport = computed(() =>
    banners.value.filter((banner) => banner.notificationType === BANNER_NOTIFICATION_MAINTENANCE));

  function setBannerGroups(nextGroups: Banner[][]): void {
    bannerGroups.value = nextGroups;
  }

  function setVisibility(nextVisibility: boolean): void {
    isVisible.value = nextVisibility;
  }

  function showBanners(): void {
    setVisibility(true);
  }

  function hideBanners(): void {
    setVisibility(false);
  }

  function startBannerRequest(): number {
    const requestId = bannerRequests.start();
    error.value = null;
    isLoading.value = true;
    return requestId;
  }

  function abandonBannerRequest(requestId: number): void {
    if (!bannerRequests.abandon(requestId)) {
      return;
    }

    isLoading.value = false;
  }

  async function fetchBanners(
    api: TrustSiteApi = new TrustSiteApi(),
    requestId = startBannerRequest(),
  ): Promise<boolean> {
    try {
      const response = await api.service().statusBanner();
      if (!bannerRequests.isCurrent(requestId)) {
        return false;
      }

      setBannerGroups(extractBannerGroups(response));
      return true;
    } catch (err) {
      if (!bannerRequests.isCurrent(requestId)) {
        return false;
      }

      error.value = getErrorMessage(err);
      throw err;
    } finally {
      if (bannerRequests.isCurrent(requestId)) {
        isLoading.value = false;
      }
    }
  }

  function $reset(): void {
    bannerRequests.invalidate();
    bannerGroups.value = [];
    isVisible.value = true;
    isLoading.value = false;
    error.value = null;
  }

  return {
    bannerGroups,
    isVisible,
    isLoading,
    error,
    banners,
    hasBanners,
    emergencyReport,
    maintenanceReport,
    setBannerGroups,
    setVisibility,
    showBanners,
    hideBanners,
    startBannerRequest,
    abandonBannerRequest,
    fetchBanners,
    $reset,
  };
});